import { useContext, useEffect, useRef, useState } from "react";
import type { ConversationDTO, FileDTO } from "../../shared/types";
import { MobileNavContext, useUser } from "../App";
import { AgentList, AgentSidebar, useChats } from "../components/AgentList";
import { FileChip } from "../components/Files";
import { IconButton } from "../components/IconButton";
import { Drawer } from "../components/Dialog";
import { TextLink } from "../components/TextLink";
import { Transcript } from "../components/Transcript";
import { Avatar } from "../components/ui";
import { ApiError, get, post, uploadFile } from "../lib/api";
import { navigate, useLocation } from "../lib/router";
import { useConversation } from "../lib/useConversation";

type Attachment = { key: string; name: string; mime: string; size: number; file?: FileDTO; error?: string };

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 19V5M6 11l6-6 6 6" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-3.5" fill="currentColor" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="3" />
    </svg>
  );
}

/** The sidebar's rows, the row this page is on, and the conversation it opens. */
function useChatsFor(conversationId: string | undefined, agentSlug: string | undefined, started: string | undefined) {
  const [open, setOpen] = useState<string | undefined>(conversationId);
  const chats = useChats(open);
  const chat = agentSlug ? chats.find((c) => c.slug === agentSlug) : undefined;
  const id = conversationId ?? chat?.conversationId ?? started;
  useEffect(() => {
    if (id !== open) setOpen(id);
  }, [id]);
  return [chats, chat, id] as const;
}

export function ChatPage({ conversationId, agentSlug }: { conversationId?: string; agentSlug?: string }) {
  const me = useUser();
  const [started, setStarted] = useState<string | undefined>(undefined);
  // /a/<agent> is the one chat with that agent; /c/<id> is a direct link, including to an earlier chat.
  const [chats, chat, openId] = useChatsFor(conversationId, agentSlug, started);
  const state = useConversation(openId);
  const mobileNav = useContext(MobileNavContext);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [dragging, setDragging] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);

  // The page stays mounted while you move between agents, so it empties what belonged to the chat you left.
  const chatKey = conversationId ?? agentSlug ?? "";
  useEffect(() => {
    setText("");
    setAttachments([]);
    setError(null);
    setStarted(undefined);
  }, [chatKey]);

  // The Draft button of a planned skill (AIT-106): its brief in the box, unsent, read from the item and never from the
  // address, and the address cleaned so a reload does not bring it back.
  const { search } = useLocation();
  const draftOf = search.get("wishlist");
  useEffect(() => {
    if (!draftOf) return;
    let current = true;
    get<{ text: string }>(`/api/wishlist/${encodeURIComponent(draftOf)}/draft`).then(
      (draft) => {
        if (!current) return;
        setText(draft.text);
        navigate(location.pathname, true);
      },
      (err) => current && setError((err as Error).message),
    );
    return () => {
      current = false;
    };
  }, [draftOf, chatKey]);

  // No agent and no conversation asked for: Pandito, which everyone has.
  useEffect(() => {
    if (!conversationId && !agentSlug && chats.length) navigate(`/a/${chats[0]!.slug}`, true);
  }, [conversationId, agentSlug, chats.length]);

  // An agent in the address bar that is not one of their rows: Pandito is how they reach a specialist.
  const unknownAgent = Boolean(agentSlug && chats.length > 0 && !chat);
  const conversation = state.conversation;
  // A chat that was folded into the live one is kept as a record, not as a place: every link to it — a card's
  // deep link, a bookmark, an admin page — opens the chat its messages are in now. Their own goes to the agent's
  // row; somebody else's, which an admin may read, goes to the conversation itself.
  const mergedInto = conversation?.mergedAt ? (conversation.supersededBy ?? null) : null;
  useEffect(() => {
    // The rows decide which of the two it is, so wait for them: with none loaded yet every chat looks like
    // somebody else's and their own would open by id, with no row highlighted.
    if (!mergedInto || chats.length === 0) return;
    const mine = chats.find((c) => c.conversationId === mergedInto);
    navigate(mine ? `/a/${mine.slug}` : `/c/${mergedInto}`, true);
  }, [mergedInto, chats.length]);
  const row = chat ?? chats.find((c) => c.agentId === conversation?.agentId);
  const agentName = row?.name ?? "Pandito";
  const agentAvatar = row?.avatarUrl ?? null;
  // An earlier chat is kept exactly as it was, and somebody else's chat is theirs to answer in: both are read-only.
  const readOnly = Boolean(conversation && (conversation.supersededBy || conversation.userId !== me.id));
  const liveChat = chats.find((c) => c.agentId === conversation?.agentId);
  const active = Object.values(state.runs).some((r) => r.status === "queued" || r.status === "running");
  const uploading = attachments.some((a) => !a.file && !a.error);
  const ready = attachments.filter((a) => a.file);
  const hasContent = Boolean(text.trim()) || ready.length > 0;
  const canSend = hasContent && !sending && !uploading && !readOnly && !unknownAgent;
  const showStop = active && Boolean(openId) && !hasContent;

  // Grow the textarea with its content up to a max height.
  useEffect(() => {
    const el = textarea.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [text]);

  function addFiles(list: FileList | File[]) {
    for (const f of Array.from(list)) {
      const key = `${f.name}-${f.size}-${Math.random()}`;
      setAttachments((cur) => [...cur, { key, name: f.name || "pasted-image.png", mime: f.type || "application/octet-stream", size: f.size }]);
      const named = f.name ? f : new File([f], `pasted-${Date.now()}.png`, { type: f.type });
      uploadFile<FileDTO>(named, openId).then(
        (file) => setAttachments((cur) => cur.map((a) => (a.key === key ? { ...a, file, name: file.name } : a))),
        (err) => setAttachments((cur) => cur.map((a) => (a.key === key ? { ...a, error: (err as Error).message } : a))),
      );
    }
  }

  async function send() {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const res = await post<{ conversation: ConversationDTO }>("/api/chat", {
        conversationId: openId, text: text.trim(), fileIds: ready.map((a) => a.file!.id), agent: openId ? undefined : agentSlug,
      });
      setText("");
      setAttachments([]);
      // Their first message to this agent created the one conversation they have with it.
      if (!openId) setStarted(res.conversation.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to send");
    } finally {
      setSending(false);
      textarea.current?.focus();
    }
  }

  return (
    <div className="flex h-full">
      {/* Only an admin has agents to choose between: everyone else has their chat with Pandito. */}
      {me.role === "admin" && (
        <>
          <AgentSidebar chats={chats} activeAgentId={conversation?.agentId ?? chat?.agentId} />
          <Drawer open={mobileNav.open} onClose={() => mobileNav.setOpen(false)}>
            <div className="border-b border-line px-4 py-3 font-semibold">Agents</div>
            <AgentList chats={chats} activeAgentId={conversation?.agentId ?? chat?.agentId} onNavigate={() => mobileNav.setOpen(false)} />
          </Drawer>
        </>
      )}

      <div
        className="relative flex min-w-0 flex-1 flex-col"
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes("Files")) {
            e.preventDefault();
            setDragging(true);
          }
        }}
        onDragLeave={(e) => {
          if (e.currentTarget === e.target) setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
        }}
      >
        {dragging && (
          <div className="pointer-events-none absolute inset-2 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-faint bg-surface/80 text-sm font-medium">
            Drop files to attach
          </div>
        )}
        {readOnly && (
          <div className="border-b border-line bg-fill-subtle px-4 py-2 text-xs text-muted">
            {conversation?.supersededBy ? (
              <>
                An earlier chat.{" "}
                {liveChat && <TextLink to={`/a/${liveChat.slug}`}>Your chat with {liveChat.name}</TextLink>} is where it was folded into.
              </>
            ) : (
              <>Someone else's conversation. You can read it; only they can answer in it.</>
            )}
          </div>
        )}
        <div className="scroll-thin flex-1 overflow-auto">
          {unknownAgent ? (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center text-sm text-muted">
              You have no chat with that agent. Ask {chats[0]?.name ?? "Pandito"}, which brings in the right specialist.
            </div>
          ) : !openId ? (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center">
              {agentAvatar ? (
                <img src={agentAvatar} alt="" className="size-14 rounded-2xl object-cover shadow-sm ring-1 ring-line" />
              ) : (
                <Avatar name={agentName} size="lg" />
              )}
              <h2 className="mt-3 text-heading font-semibold">{agentName}</h2>
              <p className="mt-1 max-w-md text-sm text-muted">
                Ask for anything, attach files or images. {agentName} will do it or delegate to the right specialist agent, live.
              </p>
            </div>
          ) : state.loading ? (
            <p className="p-8 text-sm text-muted">Loading…</p>
          ) : state.error ? (
            <p className="p-8 text-sm text-danger">{state.error}</p>
          ) : (
            <Transcript state={state} />
          )}
        </div>

        <div className={`px-3 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-6 md:pb-5 ${readOnly || unknownAgent ? "hidden" : ""}`}>
          <div className="mx-auto max-w-3xl">
            {attachments.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5 px-2">
                {attachments.map((a) => (
                  <span key={a.key} title={a.error}>
                    <FileChip
                      file={{ name: a.error ? `${a.name} — ${a.error}` : a.name, mime: a.mime, size: a.size }}
                      busy={!a.file && !a.error}
                      onRemove={() => setAttachments((cur) => cur.filter((x) => x.key !== a.key))}
                    />
                  </span>
                ))}
              </div>
            )}

            <form
              className="flex items-end gap-1 rounded-[28px] border border-line bg-surface p-1.5 shadow-[0_2px_16px_rgba(0,0,0,0.06)] transition-colors focus-within:border-line"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                ref={fileInput}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.length) addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
              <IconButton label="Attach files" fill="none" onClick={() => fileInput.current?.click()}>
                <PlusIcon />
              </IconButton>

              <textarea
                ref={textarea}
                rows={1}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onPaste={(e) => {
                  const images = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith("image/"));
                  if (images.length) {
                    e.preventDefault();
                    addFiles(images);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && !("ontouchstart" in window)) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={`Ask ${agentName}`}
                aria-label={`Message ${agentName}`}
                className="scroll-thin max-h-[200px] min-w-0 flex-1 resize-none self-center bg-transparent px-1 py-2 text-base leading-6 outline-none placeholder:text-faint md:text-reading"
              />

              {showStop ? (
                <IconButton label="Stop" fill="black" onClick={() => post(`/api/conversations/${openId}/stop`)}>
                  <StopIcon />
                </IconButton>
              ) : (
                <IconButton
                  type="submit"
                  label={uploading ? "Waiting for uploads" : "Send"}
                  title={uploading ? "Waiting for uploads…" : "Send"}
                  fill="black"
                  disabled={!canSend}
                >
                  {sending || uploading ? <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <ArrowUpIcon />}
                </IconButton>
              )}
            </form>

            {error ? (
              <p className="mt-2 px-3 text-sm text-danger">{error}</p>
            ) : (
              <p className="mt-1.5 hidden text-center text-tiny text-faint md:block">Enter to send · Shift+Enter for a new line · drop or paste files to attach</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
